import React, { useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { contactAPI } from '../services/api';
import { Mail, Phone, Trash2, Eye, X, Clock, CheckCheck, Archive, Inbox } from 'lucide-react';

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived';
  adminNotes?: string;
  createdAt: string;
}

const STATUS_STYLES: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  read: 'bg-gray-100 text-gray-700',
  replied: 'bg-green-100 text-green-700',
  archived: 'bg-yellow-100 text-yellow-700',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  new: <Inbox className="w-3 h-3" />,
  read: <Eye className="w-3 h-3" />,
  replied: <CheckCheck className="w-3 h-3" />,
  archived: <Archive className="w-3 h-3" />,
};

const AdminContact: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Contact | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchContacts = async (status?: string) => {
    setLoading(true);
    try {
      const res = await contactAPI.getAll(status ? { status } : {});
      setContacts(res.data.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(filter || undefined); }, [filter]);

  const openContact = async (c: Contact) => {
    setSelected(c);
    setNotes(c.adminNotes || '');
    // mark as read locally immediately
    if (c.status === 'new') {
      setContacts(prev => prev.map(x => x._id === c._id ? { ...x, status: 'read' } : x));
      try { await contactAPI.getById(c._id); } catch { /* triggers read on backend */ }
    }
  };

  const updateStatus = async (status: string) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await contactAPI.updateStatus(selected._id, { status, adminNotes: notes });
      const updated = res.data.data;
      setContacts(prev => prev.map(x => x._id === updated._id ? updated : x));
      setSelected(updated);
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (id: string) => {
    if (!window.confirm('Delete this message?')) return;
    await contactAPI.delete(id);
    setContacts(prev => prev.filter(x => x._id !== id));
    if (selected?._id === id) setSelected(null);
  };

  const counts = {
    all: contacts.length,
    new: contacts.filter(c => c.status === 'new').length,
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Contact Messages</h1>
          <p className="text-gray-500 text-sm mt-1">
            {counts.all} total · <span className="text-blue-600 font-medium">{counts.new} new</span>
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['', 'new', 'read', 'replied', 'archived'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                filter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-6">
          {/* List */}
          <div className="flex-1 min-w-0 space-y-3">
            {loading ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">No messages found</div>
            ) : (
              contacts.map(c => (
                <div
                  key={c._id}
                  onClick={() => openContact(c)}
                  className={`p-4 rounded-xl border cursor-pointer transition hover:shadow-md ${
                    selected?._id === c._id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'
                  } ${c.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{c.name}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[c.status]}`}>
                          {STATUS_ICONS[c.status]} {c.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{c.email}{c.phone ? ` · ${c.phone}` : ''}</p>
                      <p className="text-sm text-gray-700 font-medium mt-1 truncate">{c.subject}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{c.message}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        onClick={e => { e.stopPropagation(); deleteContact(c._id); }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-96 shrink-0 bg-white border border-gray-200 rounded-xl p-6 h-fit sticky top-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="font-bold text-gray-900 text-lg leading-tight">{selected.subject}</h2>
                <button onClick={() => setSelected(null)} className="p-1 text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="font-medium text-gray-900">{selected.name}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Mail className="w-4 h-4 shrink-0" />
                  <a href={`mailto:${selected.email}`} className="hover:text-blue-600 truncate">{selected.email}</a>
                </div>
                {selected.phone && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Phone className="w-4 h-4 shrink-0" />
                    <a href={`tel:${selected.phone}`} className="hover:text-blue-600">{selected.phone}</a>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-400">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>{new Date(selected.createdAt).toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm text-gray-700 whitespace-pre-wrap">
                {selected.message}
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Add internal notes..."
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {(['replied', 'archived'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => updateStatus(s)}
                    disabled={saving || selected.status === s}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 ${
                      s === 'replied'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                    }`}
                  >
                    {STATUS_ICONS[s]}
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContact;
